// packages/shared-types/types.ts
// Single source of truth for all EverydayOS domain types.
// Source: apps/household-app/src/types/index.ts (v2.3 Hybrid Scoping Build)

// ─── Enums & Union Types ──────────────────────────────────────────────────────

export enum Role { OWNER='OWNER', ADMIN='ADMIN', MEMBER='MEMBER', GUEST='GUEST' }
export enum SafetyMode { STRICT='strict', RELAXED='relaxed', CHILD='child' }
export type TaskStatus = 'backlog'|'todo'|'in_progress'|'pending_approval'|'done'|'pantry'|'history'|'finalized'
  |'TODO'|'IN_PROGRESS'|'REVIEW'|'DONE'|'HISTORY'
export enum TemplateType { GROCERY='grocery', ACTION='action', MAINTENANCE='maintenance' }
export type PriorityTier = 'P1'|'P2'|'P3'|'none'
export type TaskPriority = PriorityTier
export type TaskScope = 'personal'|'household'
export type HouseholdType = 'FAMILY'|'ROOMMATES'|'COUPLE'|'SHARED_PROPERTY'|'OTHER'
export type TaskSource = 'manual'|'pet'|'vehicle'|'maintenance'|'system'
export type RecurrenceRangeType = 'days'|'weeks'|'months'|'years'|'decades'
export type DataScope = 'PERSONAL'|'SHARED'|'BOTH'
export type FocusLevel = 'deep'|'shallow'|'automated'
export type ContactCategory = 'professional'|'personal'|'emergency'|'staff'

// ─── UTS: New canonical types ─────────────────────────────────────────────────
export type RebootPolicy = 'reset' | 'archive' | 'persist'
export type TaskActionType =
  | 'CREATE' | 'MOVE' | 'DELETE' | 'UPDATE'
  | 'CONSUME' | 'MIGRATE' | 'RECUR' | 'SCOPE_MIGRATE' | 'SYNC'

/** Firestore-level permission keys checked in PermissionGate and taskActions */
export const TaskPermission = {
  CAN_VIEW_LOGS: 'can_view_logs',
  CAN_MOVE_TASKS: 'can_move_tasks',
  CAN_MANAGE_HOUSEHOLD: 'can_manage_household',
} as const
export type TaskPermissionKey = typeof TaskPermission[keyof typeof TaskPermission]
export type ContactStream = TaskSource|'childcare'|'medical'|'legal'|'general'
export enum SubscriptionTier { FREE='FREE', BASIC='BASIC', PREMIUM='PREMIUM' }
export type SubscriptionModule = 'DASHBOARD'|'TASKS'|'CALENDAR'|'INVENTORY'|'MAINTENANCE'|'CONTACTS'|'AUDIT'|'SETTINGS'

export interface FeatureDef {
  id: string;
  label: string;
  path: string;
  minTier: SubscriptionTier;   // FREE for pure add-ons (tier gate passes, add-on gate is the real check)
  hideable: boolean;
  featureCategory?: FeatureCategory;
}

export const FEATURE_REGISTRY: FeatureDef[] = [
  { id: 'dashboard',    label: 'Dashboard',     path: '/dashboard',    minTier: SubscriptionTier.FREE,    hideable: false },
  { id: 'tasks',        label: 'Tasks',          path: '/tasks',        minTier: SubscriptionTier.FREE,    hideable: true  },
  { id: 'inventory',    label: 'Inventory',      path: '/groceries',    minTier: SubscriptionTier.FREE,    hideable: true  },
  { id: 'calendar',     label: 'Calendar',       path: '/calendar',     minTier: SubscriptionTier.FREE,    hideable: true  },
  { id: 'contacts',     label: 'Contacts',       path: '/contacts',     minTier: SubscriptionTier.BASIC,   hideable: true  },
  { id: 'maintenance',  label: 'Maintenance',    path: '/maintenance',  minTier: SubscriptionTier.BASIC,   hideable: true  },
  { id: 'meal-planner', label: 'Meal Planner',   path: '/meal-planner', minTier: SubscriptionTier.BASIC,   hideable: true  },
  { id: 'documents',    label: 'Document Vault', path: '/documents',    minTier: SubscriptionTier.PREMIUM, hideable: true  },
  { id: 'assistant',    label: 'AI Assistant',   path: '/assistant',    minTier: SubscriptionTier.PREMIUM, hideable: true  },
  { id: 'pets',     label: 'Pet Care',              path: '/pets',     minTier: SubscriptionTier.BASIC, hideable: true },
  { id: 'vehicles', label: 'Vehicles',              path: '/vehicles', minTier: SubscriptionTier.BASIC, hideable: true },
  { id: 'health',   label: 'Health',                 path: '/health',   minTier: SubscriptionTier.BASIC, hideable: true },
  { id: 'analytics', label: 'Analytics', path: '/analytics', minTier: SubscriptionTier.BASIC, hideable: true },
  { id: 'finance',   label: 'Finance',   path: '/finance',   minTier: SubscriptionTier.BASIC, hideable: true },
];

// ─── Supporting Interfaces ────────────────────────────────────────────────────

export interface WidgetLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface PermissionFlags {
  canEdit: boolean;
  canInvite: boolean;
  canDelete: boolean;
  [key: string]: boolean;
}

export interface ContactPhone { type: string; number: string; }
export interface ContactEmail { type: string; address: string; }
export interface ContactAddress { type: string; street: string; city: string; state: string; zip: string; }
export interface SubTask { id: string; title: string; completed: boolean; }
export interface AssetConsumable { id: string; name: string; quantity: number; }
export interface TaskDocument { id: string; title: string; url: string; }
export interface PollOption {
  id: string;
  text: string;
  votes: number;
}

export interface FeaturePoll {
  id: string;
  title: string;
  description?: string;
  options: PollOption[];
  isActive: boolean;
  minTierToVote: SubscriptionTier;
  createdAt: any;
  closedAt?: any;
  linkedFeedbackIds?: string[];
  totalVotes?: number;
}
export interface DailySpark { id: string; title: string; content: string; }
export interface Intelligence { sparks: DailySpark[]; [key: string]: any; }
export interface BoardConfig { columns: string[]; }

// ─── Core Entities ────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: Role;
  createdAt: string;
  statusMessage?: string;
  lastSeenVersion?: string;
  photoURL?: string;
  // Dev-app compat extensions
  safetyMode?: string;
  language?: string;
  cookieConsent?: any;
  avatar?: string;
  timezone?: string;
  activeSparkCategories?: string[];
  acceptedDisclaimerVersion?: string;
  acceptedLegalVersion?: string;
  legalConsentDate?: any;
  disclaimerAcceptedAt?: any;
  preferredLanguage?: string;
  autoDetectTz?: boolean;
  isInfluencer?: boolean;
  referredBy?: string;
  homeAddress?: string;
  hiddenFeatures?: string[];
  routines?: {
    bedtime?: {
      enabled: boolean;
      targetTime: string; // 'HH:MM' 24h format
      steps: Array<{
        offsetMins: number; // negative offset from bedtime
        label: string;
      }>;
    };
  };
  defaultScope?: DataScope;
  elderModeEnabled?: boolean;
  elderFontSize?: 'medium' | 'large' | 'xl';
}

export interface HouseholdMember {
  userId: string;
  role: Role;
  name?: string;
  email?: string;
  joinedAt?: string;
}

export interface HouseholdSubscription {
  tier: SubscriptionTier;
  modules: SubscriptionModule[];
  enabledAddOns?: string[];   // feature IDs of purchased add-on modules e.g. ['pets', 'vehicles']
}

export interface Household {
  id: string;
  name: string;
  type?: string;
  members: HouseholdMember[];
  memberIds?: string[];
  permissions: Record<string, PermissionFlags>;
  activeInvite?: {
    code: string;
    expiresAt: string;
    createdBy: string;
    createdAt: string;
  } | null;
  subscription?: HouseholdSubscription;
  plan?: string;
  property?: {
    purchasePrice?: number;
    purchaseDate?: string;
    currentEstimatedValue?: number;
    lastValueUpdateDate?: string;
    mortgageBalance?: number;
    mortgageLastUpdated?: string;
    squareFootage?: number;
    yearBuilt?: number;
    propertyType?: 'house' | 'condo' | 'townhouse' | 'apartment' | 'other';
    equityHistory?: Array<{ date: string; estimatedValue: number; mortgageBalance: number }>;
  };
  commuteBaseMinutes?: number;
  coParentingLinkId?: string;
  subscribedMunicipalityIds?: string[];
  linkedCareProfileIds?: string[];
}

export interface KanbanTask {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  templateType: TemplateType;
  assignedTo: string;
  lastBoardId: string | null;
  focusLevel: FocusLevel;
  priority?: PriorityTier;
  scope?: TaskScope;
  source?: TaskSource;
  subTasks?: SubTask[];
  documents?: TaskDocument[];
  dueDate?: string;
  createdAt?: string;
  visibility?: string;
  ownerUserId?: string;
  isChildSafe?: boolean;
  parentId?: string;
  assignedUserIds?: string[];
  completedAt?: string | number;
  createdBy?: string;

  // ─── UTS Cat 2: Temporal ──────────────────────────────────────────────────
  isBaseline?: boolean;
  rebootPolicy?: RebootPolicy;
  estimatedTime?: number;
  actualTime?: number;
  recurrence?: {
    isEnabled: boolean;
    interval?: number;
    frequency?: RecurrenceRangeType | null;
    completionTrigger?: boolean;
    dayOfMonth?: number;
    lastResetDate?: string;
  };

  // ─── UTS Cat 3: Bio-Metric ───────────────────────────────────────────────
  energyRequired?: number;
  moodImpact?: number;

  // ─── UTS Cat 4: Advanced Functional ─────────────────────────────────────
  tags?: string[];
  monetaryValue?: number;
  contributionPoints?: number;
  dependencies?: string[];

  // ─── UTS Cat 5: Permission & Visibility ──────────────────────────────────
  visibilityRole?: Role;
  isLocked?: boolean;
  auditTrail?: AuditTrailEntry[];

  // ─── Rotating Assignment ─────────────────────────────────────────────────
  rotation?: {
    enabled: boolean;
    memberIds: string[];
    currentIndex: number;
  };

  // ─── Approval Workflow ────────────────────────────────────────────────────
  resolvedPendingApproval?: boolean;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string | Date;
  end: string | Date;
  seriesId?: string;
  attendees?: string[];
  metadata?: { color?: string; [key: string]: any };
  description?: string;
  allDay?: boolean;
  // Dev-app compat extensions
  ownerId?: string;
  visibility?: string;
  location?: string;
  isSlice?: boolean;
  isContinued?: boolean;
  isContinuation?: boolean;
  category?: 'medical' | 'dental' | 'therapy' | 'specialist' | 'general' | string;
  arrivalBufferMins?: number; // default 15
  colorId?: string;
  recurrence?: string[];
  recurringEventId?: string;
  originalStartTime?: { dateTime: string; timeZone: string };
  isRecurrenceInstance?: boolean;
  lastModifiedBy?: string;
}

export interface EngineEvent {
  id: string;
  type: string;
  payload: any;
  timestamp: string;
}

export interface HouseholdContact {
  id: string;
  firstName: string;
  lastName: string;
  category: ContactCategory;
  stream: ContactStream;
  phones?: ContactPhone[];
  emails?: ContactEmail[];
  addresses?: ContactAddress[];
  linkedMaintenanceIds?: string[];
}

export interface InventoryItem {
  id: string;
  name: string;
  quantity: number | string;
  category?: string;
  location?: string;
  householdId?: string;
  priority?: string;
  unit?: string;
  notes?: string;
  addedByName?: string;
  lastUpdated?: string;
}

export interface AuditTrailEntry {
  actorId: string
  actionType: TaskActionType
  timestamp: number
  note?: string
}

export interface PendingSyncEntry {
  taskId: string
  scope: TaskScope
  updates: Partial<KanbanTask>
  localChangeTimestamp: number
  retryCount: number
}

export interface AuditLogEntry {
  // Core identity
  id: string
  timestamp: string | number
  scope?: TaskScope

  // Actor
  actorId: string
  userId?: string         // backward-compat
  userName?: string       // backward-compat

  // Action
  actionType: TaskActionType
  action?: string         // backward-compat

  // Target
  targetId?: string
  entityType?: 'TASK' | 'EVENT' | 'MEMBER' | 'SYSTEM'  // backward-compat
  entityName?: string     // backward-compat
  taskTitle?: string

  // State transition
  previousState?: string
  newState?: string
  fromStatus?: TaskStatus
  toStatus?: TaskStatus
  fromBoardId?: string
  toBoardId?: string

  // Sync metadata
  syncType?: 'realtime' | 'reconciliation'
  originatingDevice?: string
  isOfflineSync?: boolean

  details?: any
  metadata?: Record<string, any>
}

export interface LayoutNotification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  type?: string;
  timestamp?: string;
  // Dev-app compat extensions
  readBy?: string[];
  userId?: string;
  category?: string;
  isRead?: boolean;
  link?: string;
}

// Backward-compat aliases
export type Task = KanbanTask;
export type HouseholdPlan = string;

// ─── Legacy Add-On Types ──────────────────────────────────────────────────────

export type VehicleType = 'car'|'truck'|'suv'|'van'|'motorcycle';
export type VehicleFuelType = 'gas'|'diesel'|'electric'|'hybrid'|'GAS'|'DIESEL'|'ELECTRIC'|'HYBRID';

export interface Vehicle {
  id: string;
  name: string;
  type: VehicleType;
  fuelType: VehicleFuelType;
  make?: string;
  model?: string;
  year?: number;
  householdId?: string;
  licensePlate?: string;
  vin?: string;
  currentMileage?: number;
  mileageHistory?: { date: string; mileage: number }[];
  isChildVisible?: boolean;
  createdBy?: string;
}

export interface VehicleBooking {
  id: string;
  vehicleId: string;
  userId: string;
  start: string;
  end: string;
}

export type PetSpecies = 'dog'|'cat'|'bird'|'reptile'|'other'|'DOG'|'CAT'|'BIRD'|'REPTILE'|'OTHER';
export type PetCareType = 'feed'|'walk'|'groom'|'medicate'|'vet';

export interface PetMedication { id: string; name: string; dosage: string; frequency: string; }

export interface Pet {
  id: string;
  name: string;
  species: PetSpecies;
  medications?: PetMedication[];
  householdId?: string;
  breed?: string;
  birthdateOrAge?: string;
}

export interface PetCareTask {
  id: string;
  petId: string;
  careType: PetCareType;
  scheduledTime: string;
  completed: boolean;
}

export interface PetMedicalRecord {
  id: string;
  petId: string;
  householdId: string;
  type: 'vet_visit' | 'vaccination' | 'medication' | 'other';
  date: string;
  notes?: string;
  nextDueDate?: string;
  veterinarianContactId?: string;
  documentIds?: string[];
  cost?: number;
}

export type MaintenanceTargetType = 'vehicle'|'home'|'appliance'|'VEHICLE'|'HOME'|'APPLIANCE';
export type MaintenanceType = 'repair'|'inspection'|'cleaning'|'replace';

export interface MaintenanceSchedule {
  id: string;
  targetId: string;
  targetType: MaintenanceTargetType;
  taskTitle: string;
  frequencyMonths: number;
  householdId?: string;
  title?: string;
  nextDueDate?: string;
  requiredItems?: string[];
}

export interface MaintenanceLog {
  id: string;
  targetId: string;
  targetType: MaintenanceTargetType;
  type: MaintenanceType;
  date: string;
  notes?: string;
  cost?: number;
  householdId?: string;
  title?: string;
  description?: string;
  mileage?: number;
  contactIds?: string[];
}

// ─── Rewards & Gamification ───────────────────────────────────────────────────

export interface HouseholdReward {
  id: string;
  householdId: string;
  name: string;
  pointCost: number;
  emoji?: string;
  isActive: boolean;
  createdBy: string;
}

export interface RewardRedemption {
  id: string;
  householdId: string;
  rewardId: string;
  rewardName: string;
  memberId: string;
  memberName: string;
  pointCost: number;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

// ─── Bills & Subscriptions ────────────────────────────────────────────────────

export type BillCycle = 'monthly' | 'quarterly' | 'annual' | 'one-time';
export type BillCategory = 'streaming' | 'utilities' | 'insurance' | 'subscriptions' | 'home_services' | 'internet' | 'phone' | 'other';

export interface RecurringBill {
  id: string;
  householdId: string;
  name: string;
  category: BillCategory;
  amount: number;
  currency: string;
  billingCycle: BillCycle;
  nextDueDate: string;
  autoRenews: boolean;
  paymentMethod?: string;
  notes?: string;
  isActive: boolean;
  reminderDaysBefore: number;
  addedBy: string;
  createdAt: string;
}

// ─── AppState ─────────────────────────────────────────────────────────────────

export interface AppState {
  currentUser: UserProfile | null;
  currentHouseholdId: string | null;
  activeDataScope: DataScope;
  households: Household[];
  tasks: KanbanTask[];
  userProfiles: Record<string, UserProfile>;
  inventory: InventoryItem[];
  personalEvents: CalendarEvent[];
  householdEvents: CalendarEvent[];
  customEvents: CalendarEvent[];
  maintenanceTasks: KanbanTask[];
  notifications: LayoutNotification[];
  theme: string;
  isBooting: boolean;
  contacts: HouseholdContact[];
  dailySpark: DailySpark | null;
  lastSyncTimestamp: number | null;
  intelligence: Intelligence;
  auditLogs: AuditLogEntry[];
  dashboardLayout: WidgetLayout[];
  customGroceryCategories: {
    personal: Record<string, string[]>;
    household: Record<string, string[]>;
  };

  // Optional legacy fields
  calendarEvents?: CalendarEvent[];
  calendarSeries?: any[];
  householdSeries?: any[];
  personalSeries?: any[];
  vehicles?: Vehicle[];
  vehicleBookings?: VehicleBooking[];
  pets?: Pet[];
  petCareTasks?: PetCareTask[];
  maintenanceSchedules?: MaintenanceSchedule[];
  maintenanceLogs?: MaintenanceLog[];
  bills?: RecurringBill[];
  petMedicalRecords?: PetMedicalRecord[];
}

// ─── AppAction ────────────────────────────────────────────────────────────────

export type AppAction =
  | { type: 'SET_USER'; payload: UserProfile | null }
  | { type: 'UPDATE_USER'; payload: Partial<UserProfile> }
  | { type: 'SET_BOOTING'; payload: boolean }
  | { type: 'SET_HOUSEHOLDS'; payload: Household[] }
  | { type: 'SET_CURRENT_HOUSEHOLD'; payload: string | null }
  | { type: 'ADD_HOUSEHOLD'; payload: Household }
  | { type: 'UPDATE_HOUSEHOLD'; payload: { id: string; updates: Partial<Household> } }
  | { type: 'REMOVE_HOUSEHOLD'; payload: string }
  | { type: 'SET_DATA_SCOPE'; payload: DataScope }
  | { type: 'SET_TASKS'; payload: KanbanTask[] }
  | { type: 'ADD_TASK'; payload: KanbanTask }
  | { type: 'UPDATE_TASK'; payload: { id: string; updates: Partial<KanbanTask> } }
  | { type: 'REMOVE_TASK'; payload: string }
  | { type: 'SET_INVENTORY'; payload: InventoryItem[] }
  | { type: 'ADD_INVENTORY_ITEM'; payload: InventoryItem }
  | { type: 'SET_PERSONAL_EVENTS'; payload: CalendarEvent[] }
  | { type: 'ADD_PERSONAL_EVENT'; payload: CalendarEvent }
  | { type: 'UPDATE_PERSONAL_EVENT'; payload: CalendarEvent }
  | { type: 'DELETE_PERSONAL_EVENT'; payload: string }
  | { type: 'SET_HOUSEHOLD_EVENTS'; payload: CalendarEvent[] }
  | { type: 'ADD_HOUSEHOLD_EVENT'; payload: CalendarEvent }
  | { type: 'UPDATE_HOUSEHOLD_EVENT'; payload: CalendarEvent }
  | { type: 'DELETE_HOUSEHOLD_EVENT'; payload: string }
  | { type: 'SET_MAINTENANCE_TASKS'; payload: KanbanTask[] }
  | { type: 'SET_NOTIFICATIONS'; payload: LayoutNotification[] }
  | { type: 'DELETE_NOTIFICATION'; payload: string }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'CLEAR_ALL_NOTIFICATIONS' }
  | { type: 'SET_THEME'; payload: string }
  | { type: 'TOGGLE_THEME' }
  | { type: 'SET_SYNC'; payload: number }
  | { type: 'UPDATE_LAYOUT'; payload: WidgetLayout[] }
  | { type: 'SET_CONTACTS'; payload: HouseholdContact[] }
  | { type: 'ADD_CONTACT'; payload: HouseholdContact }
  | { type: 'REMOVE_CONTACT'; payload: string }
  | { type: 'SET_AUDIT_LOGS'; payload: AuditLogEntry[] }
  | { type: 'SAVE_CUSTOM_GROCERY_CATEGORIES'; payload: any }
  | { type: 'SET_CUSTOM_GROCERY_CATEGORIES'; payload: any }
  | { type: 'UPDATE_HOUSEHOLD_MEMBER'; payload: { householdId: string; userId: string; updates: Partial<HouseholdMember> } }
  | { type: 'UPDATE_MEMBER_PERMISSIONS'; payload: { householdId: string; role: Role; permissions: PermissionFlags } }
  | { type: 'SET_VEHICLES'; payload: Vehicle[] }
  | { type: 'ADD_VEHICLE'; payload: Vehicle }
  | { type: 'UPDATE_VEHICLE'; payload: { id: string; updates: Partial<Vehicle> } }
  | { type: 'SET_PETS'; payload: Pet[] }
  | { type: 'ADD_PET'; payload: Pet }
  | { type: 'UPDATE_PET'; payload: { id: string; updates: Partial<Pet> } }
  | { type: 'SET_MAINTENANCE_LOGS'; payload: MaintenanceLog[] }
  | { type: 'ADD_MAINTENANCE_LOG'; payload: MaintenanceLog }
  | { type: 'SET_EVENTS'; payload: CalendarEvent[] }
  | { type: 'SET_VEHICLE_BOOKINGS'; payload: VehicleBooking[] }
  | { type: 'SET_BILLS'; payload: RecurringBill[] }
  | { type: 'ADD_BILL'; payload: RecurringBill }
  | { type: 'UPDATE_BILL'; payload: { id: string; updates: Partial<RecurringBill> } }
  | { type: 'SET_PET_MEDICAL_RECORDS'; payload: PetMedicalRecord[] }
  | { type: 'ADD_PET_MEDICAL_RECORD'; payload: PetMedicalRecord };

// ─── Sprint 11: Financial Intelligence ───────────────────────────────────────

export interface IncomeSource {
  id: string;
  label: string;
  amount: number;
  frequency: 'monthly' | 'bi-weekly' | 'weekly' | 'annual';
  memberId?: string;
}

export interface SavingsGoal {
  id: string;
  label: string;
  targetAmount: number;
  currentAmount: number;
  targetDate?: string;
  monthlyContribution: number;
}

export interface HouseholdLedger {
  incomeSources: IncomeSource[];
  savingsGoals: SavingsGoal[];
  spendBaseline: Record<string, number>;
  baselineLastSet?: string;
  currency: string;
  lastUpdated: string;
}

export interface PropertyProfile {
  address: string;
  estimatedValue: number;
  mortgageBalance: number;
  purchasePrice: number;
  purchaseDate: string;
  lastUpdated: string;
}

// ─── Sprint 10: Wellness, Family & Smart Routines ─────────────────────────────

export interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: 'daily' | 'twice_daily' | 'weekly' | 'as_needed';
  times: string[]; // ['08:00', '20:00']
  startDate: string; // ISO date
  endDate?: string | null;
  currentQuantity: number;
  refillThreshold: number;
  prescribedBy?: string;
  linkedContactId?: string;
  notes?: string;
  scope: 'personal' | 'household';
  assignedToMemberId?: string;
  nextRefillDate?: string;
}

export interface ScheduledReminder {
  id: string;
  userId: string;
  type: 'medication_dose' | 'refill_alert' | 'routine_step';
  scheduledFor: string; // ISO timestamp string
  payload: Record<string, any>;
  status: 'pending' | 'sent' | 'cancelled';
  createdAt: string; // ISO timestamp string
}

export interface FamilyGoal {
  id: string;
  householdId: string;
  title: string;
  targetCount: number;
  completedCount: number;
  targetFrequency: 'weekly' | 'bi_weekly' | 'monthly';
  membersRequired: string[]; // UIDs
  sessionDurationMins: number;
  createdBy: string;
  createdAt: string;
}

export type RoleView = 'admin' | 'member' | 'child';

// ─── Sprint 12: Feature Management ───────────────────────────────────────────

export type FeatureStatus = 'active' | 'maintenance' | 'disabled';
export type FeatureCategory = 'core' | 'addon';

export interface ScheduledChange {
  id: string;
  effectAt: any;
  changes: Partial<Pick<FeatureConfigDoc, 'status' | 'minTierOverride' | 'allowedScopes' | 'rolloutPercent' | 'rolloutByTier'>>;
  reason: string;
  createdBy: string;
}

export interface FeatureConfigDoc {
  featureId: string;
  featureCategory: FeatureCategory;
  status: FeatureStatus;
  maintenanceMessage?: string;
  disabledRedirectPath?: string;
  minTierOverride?: SubscriptionTier | null;
  allowedScopes?: Array<'personal' | 'household'> | null;
  addonIncludedInTiers?: SubscriptionTier[];
  rolloutPercent: number;
  rolloutByTier?: Partial<Record<SubscriptionTier, number>>;
  rolloutHouseholdIds?: string[];
  scheduledChanges?: ScheduledChange[];
  lastModifiedBy: string;
  lastModifiedAt: any;
  modificationReason: string;
}

export interface MergedFeatureConfig extends FeatureDef {
  featureCategory: FeatureCategory;
  status: FeatureStatus;
  maintenanceMessage?: string;
  disabledRedirectPath?: string;
  effectiveMinTier: SubscriptionTier;
  effectiveScopes: Array<'personal' | 'household'>;
  addonIncludedInTiers: SubscriptionTier[];
  rolloutPercent: number;
  rolloutByTier: Partial<Record<SubscriptionTier, number>>;
  rolloutHouseholdIds: string[];
  scheduledChanges: ScheduledChange[];
  lastModifiedBy?: string;
  lastModifiedAt?: any;
  modificationReason?: string;
}

export const FEATURE_DEPENDENCY_MAP: Record<string, string[]> = {
  'commute-calculator':    ['calendar'],
  'ai-scheduling':         ['calendar'],
  'post-appointment-loop': ['calendar', 'tasks'],
  'family-goals':          ['calendar', 'tasks'],
  'medication-ledger':     ['contacts'],
  'finance':               ['bills'],
  'meal-planner':          ['inventory'],
};

// ─── Sprint 12: Feedback & Polls ─────────────────────────────────────────────

export type FeedbackType = 'bug' | 'suggestion';
export type FeedbackStatus = 'new' | 'in_review' | 'planned' | 'resolved' | 'closed';
export type BugSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface UserFeedback {
  id: string;
  type: FeedbackType;
  title: string;
  area?: string;
  severity?: BugSeverity;
  description: string;
  submittedBy: string;
  submitterEmail: string;
  submitterTier: SubscriptionTier;
  submittedAt: any;
  userAgent: string;
  appVersion: string;
  status: FeedbackStatus;
  adminNotes?: string;
  linkedPollId?: string;
}

export interface PollVote {
  userId: string;
  optionId: string;
  power: number;
  votedAt: any;
}

// ─── Sprint 13: Co-Parenting ──────────────────────────────────────────────────

export type CustodyPattern = 'week-on-week-off' | '2-2-3' | '5-2-2-5' | 'custom';
export type SwapStatus = 'pending' | 'accepted' | 'declined' | 'countered' | 'withdrawn';
export type CoParentRole = 'primary' | 'secondary';
export type SharedExpenseCategory = 'medical' | 'education' | 'clothing' | 'activity' | 'other';

export interface CustodyConfig {
  weekOnWeekOff?: { startDate: string; firstHouseholdId: string };
  twoTwoThree?: { anchorDate: string };
  fiveTwoTwoFive?: { anchorDate: string; firstHouseholdId: string };
  custom?: Array<{ householdId: string; startDate: string; endDate: string }>;
  holidayOverrides?: Array<{ id: string; name: string; date: string; householdId: string }>;
}

export interface CoParentingLink {
  id: string;
  householdA: { householdId: string; label: string; role: CoParentRole };
  householdB: { householdId: string; label: string; role: CoParentRole } | null;
  custodyPattern: CustodyPattern;
  custodyConfig: CustodyConfig;
  timezone: string;
  status: 'pending' | 'active';
  inviteCode?: string;
  inviteExpiresAt?: string;
  createdAt: string;
  createdBy: string;
}

export interface ChildMedication {
  id: string;
  name: string;
  dose: string;
  schedule: string;
  prescribedBy?: string;
}

export interface ChildActivity {
  id: string;
  name: string;
  dayOfWeek: string;
  time: string;
  location?: string;
  drivingHousehold: string | 'both';
}

export interface SharedChild {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  schoolName?: string;
  schoolGrade?: string;
  photoUrl?: string;
  allergies: string[];
  medications: ChildMedication[];
  activities: ChildActivity[];
  doctorName?: string;
  doctorPhone?: string;
  emergencyContacts: Array<{ id: string; name: string; relation: string; phone: string }>;
  notes?: string;
  lastUpdatedBy: string;
  lastUpdated: string;
  pendingDeletion?: {
    requestedBy: string;
    requestedAt: string;
    confirmedBy?: string;
  };
}

export interface CustodySwapRequest {
  id: string;
  requestedBy: string;
  requestedAt: string;
  originalDate: string;
  proposedDate?: string;
  oneWay: boolean;
  reason?: string;
  status: SwapStatus;
  respondedBy?: string;
  respondedAt?: string;
  counterProposedDate?: string;
  counterReason?: string;
}

export interface SharedExpense {
  id: string;
  childId: string | 'all';
  description: string;
  amount: number;
  currency: string;
  category: SharedExpenseCategory;
  paidBy: string;
  date: string;
  receiptUrl?: string;
  addedBy: string;
  addedAt: string;
}

export interface HandoffNote {
  id: string;
  childId?: string;
  fromHouseholdId: string;
  message: string;
  isUrgent: boolean;
  handoffDate: string;
  createdAt: string;
  readBy: string[];
}

// ─── Sprint 14: Municipal Services ───────────────────────────────────────────
export type MunicipalCategory = 'garbage'|'recycling'|'organics'|'bulky'|'other'
export type MunicipalEventCategory = 'concert'|'market'|'public_meeting'|'sport'|'festival'|'other'
export type AnnouncementCategory = 'notice'|'alert'|'update'|'general'
export type AnnouncementStatus = 'published'|'draft'
export type EventStatus = 'published'|'draft'|'cancelled'
export type MunicipalStatus = 'active'|'inactive'

export interface MunicipalRecurrence {
  weekday: number;
  interval: 'weekly'|'biweekly';
  biweeklyGroup?: 'A'|'B';
  startDate: string;
}

export interface MunicipalSchedule {
  id: string;
  title: string;
  category: MunicipalCategory;
  color: string;
  recurrence: MunicipalRecurrence;
  notes?: string;
  isActive: boolean;
  createdAt: string;
}

export interface MunicipalException {
  id: string;
  scheduleId: string;
  scheduleTitle: string;
  originalDate: string;
  newDate: string|null;
  reason: string;
  createdAt: string;
}

export interface MunicipalEvent {
  id: string;
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  location: string;
  category: MunicipalEventCategory;
  imageUrl?: string;
  registrationUrl?: string;
  status: EventStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MunicipalAnnouncement {
  id: string;
  title: string;
  body: string;
  isUrgent: boolean;
  category: AnnouncementCategory;
  status: AnnouncementStatus;
  expiresAt?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Municipality {
  id: string;
  name: string;
  region: string;
  timezone: string;
  logoUrl?: string;
  contactEmail: string;
  status: MunicipalStatus;
  assignedAdminUids: string[];
  subscriberCount: number;
  createdAt: string;
  createdBy: string;
}

// ─── Sprint 15: Elder Care Types ─────────────────────────────────────────────

// ── Union Types ──────────────────────────────────────────────────────────────
export type DnrStatus = 'none' | 'full' | 'dnr-cc' | 'partial';
export type CareRole = 'care-admin' | 'contributor' | 'viewer';
export type LivingSituation = 'co-resident' | 'independent' | 'assisted-living';
export type TransportType = 'household-member' | 'medical-transport' | 'rideshare' | 'other';
export type AppointmentType = 'gp' | 'specialist' | 'dental' | 'therapy' | 'lab' | 'imaging' | 'other';
export type IncidentType = 'fall' | 'confusion' | 'breathing' | 'pain' | 'behavioural' | 'injury' | 'other';
export type MedFrequency = 'daily' | 'twice_daily' | 'three_times_daily' | 'weekly' | 'as_needed';
export type AdherenceStatus = 'taken' | 'skipped' | 'deferred';
export type TransportRequestStatus = 'requested' | 'confirmed' | 'completed' | 'cancelled';
export type AppointmentStatus = 'upcoming' | 'completed' | 'cancelled';
export type IncidentSeverity = 'minor' | 'moderate' | 'serious';
export type NoteType = 'observation' | 'concern' | 'question' | 'positive' | 'general';
export type DocumentCategory = 'dnr' | 'poa' | 'healthcare-proxy' | 'living-will' | 'insurance' | 'id' | 'legal' | 'other';
export type CareTeamRole = 'gp' | 'specialist' | 'dentist' | 'pharmacist' | 'physiotherapist' | 'home-aide' | 'social-worker' | 'other';

// ── Interfaces ───────────────────────────────────────────────────────────────
export interface DnrChangeEntry {
  changedBy: string;
  changedAt: string;
  previousStatus: DnrStatus;
  newStatus: DnrStatus;
}

export interface CareCircleMember {
  userId: string;
  name: string;
  email: string;
  role: CareRole;
  joinedAt: string;
}

export interface InsuranceInfo {
  provider: string;
  memberId: string;
  groupId?: string;
  phone?: string;
}

export interface CareProfile {
  id: string;
  memberUids: string[];
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  photoUrl?: string;
  dnrStatus: DnrStatus;
  dnrDocumentUrl?: string;
  dnrSigningDate?: string;
  dnrPhysicianName?: string;
  dnrPhysicalCopyLocation?: string;
  dnrLastReviewedDate?: string;
  dnrNextReviewDate?: string;
  dnrChangeLog: DnrChangeEntry[];
  primaryConditions: string[];
  allergies: string[];
  bloodType?: string;
  livingSituation: LivingSituation;
  facilityName?: string;
  facilityAddress?: string;
  facilityRoom?: string;
  facilityContactName?: string;
  facilityContactPhone?: string;
  mobilityAids: string[];
  mobilityNotes?: string;
  drivingStatus?: 'active' | 'restricted' | 'retired';
  dietaryRestrictions: string[];
  insurancePrimary?: InsuranceInfo;
  insuranceSecondary?: InsuranceInfo;
  careCircle: CareCircleMember[];
  linkedHouseholdIds: string[];
  elderLinkedUid?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareVital {
  id: string;
  date: string;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  heartRate?: number;
  weight?: number;
  bloodGlucose?: number;
  spo2?: number;
  temperature?: number;
  notes?: string;
  loggedBy: string;
  loggedAt: string;
}

export interface CareCheckIn {
  id: string;
  date: string;
  painLevel: 0 | 1 | 2;
  generalFeeling: 'same' | 'worse' | 'better';
  needsHelp: boolean;
  notes?: string;
  loggedBy: string;
  loggedAt: string;
}

export interface CareMedication {
  id: string;
  name: string;
  dosage: string;
  frequency: MedFrequency;
  scheduledTimes: string[];
  startDate: string;
  endDate?: string;
  currentQuantity: number;
  refillThreshold: number;
  nextRefillDate?: string;
  prescribedBy?: string;
  pharmacyName?: string;
  pharmacyPhone?: string;
  linkedCareTeamContactId?: string;
  notes?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
}

export interface MedAdherence {
  id: string;
  medicationId: string;
  medicationName: string;
  scheduledDate: string;
  scheduledTime: string;
  status: AdherenceStatus;
  takenAt?: string;
  loggedBy: string;
  notes?: string;
}

export interface CareAppointment {
  id: string;
  title: string;
  appointmentType: AppointmentType;
  providerName: string;
  providerPhone?: string;
  linkedCareTeamContactId?: string;
  dateTime: string;
  location?: string;
  companionMemberId?: string;
  companionHouseholdId?: string;
  prepNotes?: string;
  postNotes?: string;
  followUpActions?: string[];
  status: AppointmentStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CareIncident {
  id: string;
  date: string;
  type: IncidentType;
  description: string;
  location?: string;
  injuryAssessment?: string;
  medicalAttentionSought: boolean;
  medicalAttentionDetails?: string;
  severity: IncidentSeverity;
  loggedBy: string;
  loggedAt: string;
}

export interface CareTransportRequest {
  id: string;
  appointmentId?: string;
  date: string;
  time: string;
  destination: string;
  purpose?: string;
  transportType: TransportType;
  assignedMemberId?: string;
  assignedHouseholdId?: string;
  assignedMemberName?: string;
  externalProviderName?: string;
  externalProviderPhone?: string;
  status: TransportRequestStatus;
  notes?: string;
  createdBy: string;
  createdAt: string;
}

export interface CareNote {
  id: string;
  type: NoteType;
  message: string;
  isUrgent: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
}

export interface CareDocument {
  id: string;
  label: string;
  category: DocumentCategory;
  fileUrl: string;
  uploadedBy: string;
  uploadedAt: string;
  notes?: string;
}

export interface CareTeamContact {
  id: string;
  name: string;
  role: CareTeamRole;
  specialtyOrAgency?: string;
  phone: string;
  email?: string;
  address?: string;
  notes?: string;
  addedBy: string;
  createdAt: string;
}
